import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
// `motion` is aliased to `Motion` so the lowercase namespace is not read as an unused var
// by no-unused-vars (this config has no react/jsx-uses-vars).
import { AnimatePresence, motion as Motion, useReducedMotion } from "motion/react";
import Icon from "./Icon";
import { COMMAND_PALETTE_EVENT } from "./SearchInput";
import { lockBodyScroll } from "../../helpers/scrollLock";
import "./CommandPalette.scss";

// Fontaine M34 — CommandPalette (`ft-cmdk`): the one ⌘K surface. Centred dialog on laptop, bottom
// sheet on phones — one DOM, the shape is CSS (charter: prefer CSS, JS breakpoints only when markup
// must differ). Opens on ⌘K / Ctrl+K and on the shared `ft:command-palette` event that SearchInput,
// AppTopbar and AppMobileTopbar already dispatch. Esc, the scrim and running a command close it, and
// focus goes back to whatever had it before. Semantics: dialog > combobox + listbox with
// aria-activedescendant, so the active row is announced without focus ever leaving the input.
//
// The component knows nothing about routes: it renders `commands` and calls `onSelect(command)`.
// Route knowledge stays in the shell (navigation.js), which is what mounts it.

/** Subsequence match — every query character appears in order (case-insensitive). */
const commandMatches = (query, command) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const haystacks = [command.label, command.hint, command.group, ...(command.keywords || [])]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase());
  return haystacks.some((s) => {
    let i = 0;
    for (const ch of s) if (ch === q[i]) i += 1;
    return i === q.length;
  });
};

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");

/**
 * @param {Array<{id: string, label: string, hint?: string, group?: string, icon?: string,
 *                keys?: string, keywords?: string[], disabled?: boolean}>} commands
 * @param {(command) => void} [onSelect]     run the command (the shell navigates)
 * @param {boolean} [open] / [onOpenChange]  controlled; uncontrolled by default (⌘K + shared event)
 * @param {string} [placeholder]
 * @param {string} [label="Command palette"] accessible name of the dialog
 */
export default function CommandPalette({
  commands = [],
  onSelect,
  open: openProp,
  onOpenChange,
  placeholder = "Search or run a command…",
  label = "Command palette",
}) {
  const uid = useId().replace(/:/g, "");
  const listId = `ft-cmdk-${uid}-list`;
  const [inner, setInner] = useState(false);
  const open = openProp ?? inner;
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const restoreTo = useRef(null);

  const setOpen = useCallback(
    (next) => {
      if (openProp === undefined) setInner(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange]
  );

  const close = useCallback(() => setOpen(false), [setOpen]);

  const results = useMemo(() => commands.filter((c) => commandMatches(query, c)), [commands, query]);

  // Flat results carry their own index so ↑/↓ can run on one array while the rows stay grouped.
  const groups = useMemo(() => {
    const byGroup = new Map();
    results.forEach((command, flatIndex) => {
      const key = command.group || "";
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key).push({ ...command, flatIndex });
    });
    return [...byGroup.entries()].map(([group, items]) => ({ group, items }));
  }, [results]);

  // ⌘K / Ctrl+K and the shared event both OPEN (never toggle): SearchInput already turns ⌘K into
  // the event, so a toggle here would open and immediately close on the same keystroke.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    const onEvent = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener(COMMAND_PALETTE_EVENT, onEvent);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(COMMAND_PALETTE_EVENT, onEvent);
    };
  }, [setOpen]);

  // Reset on open, focus the input, lock the page behind, restore focus on close.
  useEffect(() => {
    if (!open) return undefined;
    restoreTo.current = document.activeElement;
    setQuery("");
    setActive(0);
    const release = lockBodyScroll();
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      clearTimeout(t);
      release?.();
      const back = restoreTo.current;
      if (back && typeof back.focus === "function" && document.contains(back)) back.focus();
    };
  }, [open]);

  // Keep the active row inside the list and scrolled into view.
  useEffect(() => {
    if (active > 0 && active >= results.length) setActive(Math.max(0, results.length - 1));
  }, [results.length, active]);

  useEffect(() => {
    if (!open) return;
    const row = listRef.current?.querySelector('[role="option"][aria-selected="true"]');
    row?.scrollIntoView?.({ block: "nearest" });
  }, [active, open, results.length]);

  const run = (command) => {
    if (!command || command.disabled) return;
    close();
    onSelect?.(command);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (results.length ? Math.min(i + 1, results.length - 1) : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(results[active]);
    } else if (e.key === "Tab") {
      // Single-focus surface: containment means Tab never leaves the input.
      e.preventDefault();
      inputRef.current?.focus();
    }
  };

  if (typeof document === "undefined") return null;

  const activeId = results[active] ? `${listId}-opt-${slug(results[active].id)}` : undefined;
  const fade = { duration: reduce ? 0 : 0.15 };
  const panel = reduce ? { duration: 0 } : { type: "spring", stiffness: 420, damping: 32 };

  return createPortal(
    <AnimatePresence>
      {open ? (
        <Motion.div
          className="ft-cmdk"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={fade}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <Motion.div
            className="ft-cmdk__panel"
            role="dialog"
            aria-modal="true"
            aria-label={label}
            initial={{ opacity: 0, y: reduce ? 0 : -12, scale: reduce ? 1 : 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: reduce ? 0 : -8, scale: reduce ? 1 : 0.98 }}
            transition={panel}
            onKeyDown={onKeyDown}
          >
            <span className="ft-cmdk__handle" aria-hidden="true" />
            <div className="ft-cmdk__inputrow">
              <span className="ft-cmdk__inputicon"><Icon name="Search" size="sm" /></span>
              <input
                ref={inputRef}
                type="text"
                className="ft-cmdk__input"
                role="combobox"
                aria-label={placeholder}
                aria-expanded={results.length > 0}
                aria-controls={results.length > 0 ? listId : undefined}
                aria-autocomplete="list"
                aria-activedescendant={activeId}
                autoComplete="off"
                placeholder={placeholder}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
              />
              <kbd className="ft-cmdk__kbd" aria-hidden="true">Esc</kbd>
            </div>

            <div className="ft-cmdk__results" ref={listRef}>
              {results.length === 0 ? (
                <p className="ft-cmdk__empty" role="status">
                  <span className="ft-cmdk__empty-icon" aria-hidden="true"><Icon name="Search" size="md" /></span>
                  No commands match “{query}”.
                </p>
              ) : (
                <div className="ft-cmdk__list" id={listId} role="listbox" aria-label={label}>
                  {groups.map(({ group, items }) => (
                    <div className="ft-cmdk__group" role="group" key={group || "_"} aria-labelledby={group ? `${listId}-g-${slug(group)}` : undefined}>
                      {group ? <p className="ft-cmdk__grouphead" id={`${listId}-g-${slug(group)}`}>{group}</p> : null}
                      {items.map((command) => (
                        <div
                          key={command.id}
                          id={`${listId}-opt-${slug(command.id)}`}
                          role="option"
                          aria-selected={command.flatIndex === active}
                          aria-disabled={command.disabled || undefined}
                          className={`ft-cmdk__item${command.flatIndex === active ? " is-active" : ""}${command.disabled ? " is-disabled" : ""}`}
                          onMouseMove={() => setActive(command.flatIndex)}
                          onClick={() => run(command)}
                        >
                          {command.icon ? (
                            <span className="ft-cmdk__item-icon" aria-hidden="true"><Icon name={command.icon} size="sm" /></span>
                          ) : null}
                          <span className="ft-cmdk__item-text">
                            <span className="ft-cmdk__item-label">{command.label}</span>
                            {command.hint ? <span className="ft-cmdk__item-hint">{command.hint}</span> : null}
                          </span>
                          {command.disabled ? (
                            <span className="ft-cmdk__item-lock" aria-hidden="true"><Icon name="Lock" size="xs" /></span>
                          ) : command.keys ? (
                            <kbd className="ft-cmdk__kbd">{command.keys}</kbd>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="ft-cmdk__foot" aria-hidden="true">
              <span className="ft-cmdk__hint">
                <kbd className="ft-cmdk__kbd"><Icon name="ArrowUp" size="xs" /></kbd>
                <kbd className="ft-cmdk__kbd"><Icon name="ArrowDown" size="xs" /></kbd>
                navigate
              </span>
              <span className="ft-cmdk__hint"><kbd className="ft-cmdk__kbd">Enter</kbd> run</span>
              <span className="ft-cmdk__hint"><kbd className="ft-cmdk__kbd">Esc</kbd> close</span>
            </div>
          </Motion.div>
        </Motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
}
